import React from 'react';
import { ArrowLeft, TrendingUp, TrendingDown, Users, Github, Twitter, MessageCircle } from 'lucide-react';
import type { CryptoRecommendation } from '../../types/crypto';

interface CryptoDetailsProps {
  crypto: CryptoRecommendation;
  onBack: () => void;
}

export function CryptoDetails({ crypto, onBack }: CryptoDetailsProps) {
  return (
    <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
      {/* Header */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to List
          </button>
          <h2 className="text-3xl font-bold text-white mb-2">{crypto.name}</h2>
          <p className="text-gray-400">{crypto.symbol}</p>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-400 mb-1">Market Cap</div>
          <div className="text-xl font-mono">${(crypto.marketCap / 1_000_000).toFixed(1)}M</div>
        </div>
      </div>

      {/* Price Information */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-gray-800 p-4 rounded-lg">
          <div className="text-sm text-gray-400 mb-1">All Time High</div>
          <div className="text-xl font-mono">${crypto.priceHistory.allTimeHigh}</div>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg">
          <div className="text-sm text-gray-400 mb-1">All Time Low</div>
          <div className="text-xl font-mono">${crypto.priceHistory.allTimeLow}</div>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg">
          <div className="text-sm text-gray-400 mb-1">YTD Change</div>
          <div className={`text-xl font-mono flex items-center gap-2 ${
            parseFloat(crypto.priceHistory.yearToDateChange) > 0
              ? 'text-green-400'
              : 'text-red-400'
          }`}>
            {parseFloat(crypto.priceHistory.yearToDateChange) > 0 ? (
              <TrendingUp className="w-5 h-5" />
            ) : (
              <TrendingDown className="w-5 h-5" />
            )}
            {crypto.priceHistory.yearToDateChange}
          </div>
        </div>
      </div>

      {/* Technology & Features */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div>
          <h3 className="text-lg font-bold text-white mb-4">Technology</h3>
          <div className="space-y-4">
            <div>
              <div className="text-sm text-gray-400 mb-1">Consensus</div>
              <div className="text-cyan-400">{crypto.technology.consensus}</div>
            </div>
            <div>
              <div className="text-sm text-gray-400 mb-1">Transactions per Second</div>
              <div className="text-cyan-400">{crypto.technology.tps}</div>
            </div>
            <div>
              <div className="text-sm text-gray-400 mb-1">Key Features</div>
              <ul className="list-disc list-inside space-y-1">
                {crypto.technology.features.map((feature, index) => (
                  <li key={index} className="text-cyan-400">{feature}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-bold text-white mb-4">Team & Partnerships</h3>
          <div className="space-y-4">
            <div>
              <div className="text-sm text-gray-400 mb-1">Team Type</div>
              <div className="text-cyan-400">{crypto.team.type}</div>
            </div>
            <div>
              <div className="text-sm text-gray-400 mb-1">Notable Members</div>
              <ul className="list-disc list-inside space-y-1">
                {crypto.team.notable.map((member, index) => (
                  <li key={index} className="text-cyan-400">{member}</li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-sm text-gray-400 mb-1">Partnerships</div>
              <ul className="list-disc list-inside space-y-1">
                {crypto.partnerships.map((partnership, index) => (
                  <li key={index} className="text-cyan-400">{partnership}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Community & Risk Factors */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h3 className="text-lg font-bold text-white mb-4">Community Metrics</h3>
          <div className="space-y-4">
            {crypto.communityMetrics.twitter && (
              <div className="flex items-center gap-2">
                <Twitter className="w-4 h-4 text-blue-400" />
                <span className="text-gray-400">Twitter:</span>
                <span className="text-white">{crypto.communityMetrics.twitter.toLocaleString()} followers</span>
              </div>
            )}
            {crypto.communityMetrics.reddit && (
              <div className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-orange-400" />
                <span className="text-gray-400">Reddit:</span>
                <span className="text-white">{crypto.communityMetrics.reddit.toLocaleString()} members</span>
              </div>
            )}
            {crypto.communityMetrics.github && (
              <div className="flex items-center gap-2">
                <Github className="w-4 h-4 text-purple-400" />
                <span className="text-gray-400">GitHub:</span>
                <span className="text-white">
                  {crypto.communityMetrics.github.stars.toLocaleString()} stars,
                  {' '}{crypto.communityMetrics.github.contributors} contributors
                </span>
              </div>
            )}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-bold text-white mb-4">Risk Factors</h3>
          <ul className="list-disc list-inside space-y-2">
            {crypto.riskFactors.map((risk, index) => (
              <li key={index} className="text-red-400">{risk}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}